import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Save, Edit2, X, Wrench, Calendar, Clock, MapPin, Phone,
  User, Camera, Upload, Trash2, ZoomIn, MessageSquare, Send, CheckCircle, AlertTriangle
} from 'lucide-react';

const TYPE_LABELS = { sav: 'SAV', maintenance: 'Maintenance', visite: 'Visite', autre: 'Autre' };
const PRIORITE_COLORS = { normale: 'bg-blue-100 text-blue-700', urgente: 'bg-red-100 text-red-700' };
const STATUT_CONFIG = {
  a_planifier: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'À planifier' },
  assignee: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Assignée' },
  a_reassigner: { bg: 'bg-red-100', text: 'text-red-600', label: 'À réassigner' },
  en_cours: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours' },
  terminee: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Terminée' },
  annulee: { bg: 'bg-red-100', text: 'text-red-600', label: 'Annulée' },
};

export default function InterventionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [intervention, setIntervention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showClose, setShowClose] = useState(false);
  const [compteRendu, setCompteRendu] = useState('');
  const [legende, setLegende] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [livreurs, setLivreurs] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/interventions/${id}`);
      setIntervention(res.data);
    } catch { navigate(-1); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [id]);

  useEffect(() => {
    api.get('/users').then(res => {
      setLivreurs((res.data || []).filter(u => u.role === 'livreur'));
    }).catch(() => {});
  }, []);

  const startEditing = () => {
    setEditData({
      date_prevue: intervention.date_prevue ? intervention.date_prevue.split('T')[0] : '',
      heure_debut: intervention.heure_debut || '',
      heure_fin: intervention.heure_fin || '',
      technicien_id: intervention.technicien_id || '',
      technicien_nom: intervention.technicien_nom || '',
      description: intervention.description || '',
      priorite: intervention.priorite || 'normale',
      titre: intervention.titre || '',
      type: intervention.type || 'sav',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editData };
      if (payload.technicien_id) {
        const tech = livreurs.find(l => l.id === payload.technicien_id);
        if (tech) payload.technicien_nom = `${tech.prenom || ''} ${tech.nom || ''}`.trim();
      }
      await api.put(`/interventions/${id}`, payload);
      toast.success('Intervention modifiée');
      setEditing(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSendingNote(true);
    try {
      await api.post(`/interventions/${id}/notes`, { texte: noteText.trim() });
      toast.success('Note ajoutée');
      setNoteText('');
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSendingNote(false); }
  };

  const changeStatut = async (statut, note = '') => {
    try {
      await api.put(`/interventions/${id}/statut`, { statut, note, compte_rendu: compteRendu });
      toast.success('Statut mis à jour');
      setShowClose(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const uploadFiles = async (files) => {
    const images = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!images.length) { toast.error('Seules les images sont acceptées'); return; }
    setUploadingPhoto(true);
    let ok = 0;
    for (const file of images) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('legende', legende);
        await api.post(`/interventions/${id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch (e) { console.error(e); }
    }
    if (ok) { toast.success(`${ok} photo${ok > 1 ? 's' : ''} ajoutée${ok > 1 ? 's' : ''}`); setLegende(''); fetchData(); }
    setUploadingPhoto(false);
  };

  const handleFileInput = async (e) => { await uploadFiles(e.target.files); e.target.value = ''; };
  const handleDrop = async (e) => { e.preventDefault(); setIsDragging(false); await uploadFiles(e.dataTransfer.files); };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/documents/${photoId}`);
      toast.success('Photo supprimée');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const [dp] = d.split('T');
    const [y, m, day] = dp.split('-');
    return `${day}/${m}/${y}`;
  };
  const formatTime = (d) => d ? d.split('T')[1]?.substring(0, 5) || '' : '';
  const formatFull = (d) => {
    if (!d) return '—';
    const [dp, tp] = d.split('T');
    const [y, m, day] = dp.split('-');
    return `${day}/${m}/${y} ${tp ? tp.substring(0, 5) : ''}`;
  };

  if (loading) return <div className="p-8 flex items-center justify-center"><RefreshCw size={20} className="animate-spin text-gray-400" /></div>;
  if (!intervention) return null;

  const client = intervention.client || {};
  const photos = intervention.photos || [];
  const notes = intervention.notes_terrain || [];
  const statut = STATUT_CONFIG[intervention.statut] || STATUT_CONFIG.a_planifier;
  const prioriteCls = PRIORITE_COLORS[intervention.priorite] || PRIORITE_COLORS.normale;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn">
          <ArrowLeft size={18} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Wrench size={20} className="text-orange-500" />
            <h1 className="text-xl font-bold text-[hsl(222,47%,11%)]">{intervention.titre || 'Intervention'}</h1>
          </div>
          <p className="text-sm text-gray-500">{client.societe} — {TYPE_LABELS[intervention.type] || intervention.type}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${prioriteCls}`}>
          {intervention.priorite === 'urgente' ? 'Urgente' : 'Normale'}
        </span>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statut.bg} ${statut.text}`} data-testid="statut-badge">
          {statut.label}
        </span>
      </div>

      {/* Info client + intervention */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><MapPin size={16} className="text-amber-500" /> Client</h3>
          <p className="font-semibold text-gray-900">{client.societe}</p>
          <p className="text-sm text-gray-600 mt-1">{client.adresse_livraison || client.adresse || '—'}</p>
          <p className="text-sm text-gray-600">{client.code_postal_livraison || client.code_postal} {client.ville_livraison || client.ville}</p>
          <div className="flex gap-4 mt-3 text-sm text-gray-600">
            {(client.tel_salon || client.tel_portable) && (
              <span className="flex items-center gap-1"><Phone size={14} /> {client.tel_salon || client.tel_portable}</span>
            )}
          </div>
          <button onClick={() => navigate(`/clients/${intervention.client_id}`)} className="mt-3 text-sm text-blue-600 hover:underline" data-testid="go-to-client-btn">
            Voir la fiche client
          </button>
        </div>

        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Calendar size={16} className="text-blue-500" /> Planification</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Date prévue</span><span className="font-medium">{formatDate(intervention.date_prevue)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Horaire</span><span className="font-medium">{intervention.heure_debut || '—'} - {intervention.heure_fin || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Technicien</span><span className="font-medium">{intervention.technicien_nom || '— Non assigné —'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Créée le</span><span className="font-medium">{formatDate(intervention.created_at)}</span></div>
          </div>
        </div>
      </div>

      {/* Description */}
      {intervention.description && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs font-medium text-amber-700 mb-1">Description</p>
          <p className="text-sm text-amber-900">{intervention.description}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        {intervention.statut === 'assignee' && (
          <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="confirmation-banner">
            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-800 text-sm">En attente de confirmation</p>
              <p className="text-xs text-amber-600">Le technicien a reçu un email de notification</p>
            </div>
          </div>
        )}
        {intervention.statut === 'a_reassigner' && (
          <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="reassign-banner">
            <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-800 text-sm">Le technicien a refusé — réassignation nécessaire</p>
            </div>
          </div>
        )}
        {!['terminee', 'annulee'].includes(intervention.statut) && (
          <button onClick={startEditing} className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)]" data-testid="edit-btn">
            <Edit2 size={14} /> Modifier
          </button>
        )}
        {intervention.statut === 'en_cours' && (
          <button onClick={() => setShowClose(true)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="finish-btn">
            <CheckCircle size={14} /> Terminer l'intervention
          </button>
        )}
        {!['terminee', 'annulee'].includes(intervention.statut) && (
          <button onClick={() => changeStatut('annulee', 'Intervention annulée')} className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50" data-testid="cancel-btn">
            <X size={14} /> Annuler
          </button>
        )}
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-white rounded-xl border-2 border-blue-300 p-5 shadow-sm space-y-4" data-testid="edit-form">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Edit2 size={16} className="text-blue-500" /> Modifier l'intervention</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Titre</label>
              <input type="text" value={editData.titre} onChange={e => setEditData(p => ({ ...p, titre: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-titre" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Type</label>
              <select value={editData.type} onChange={e => setEditData(p => ({ ...p, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-type">
                <option value="sav">SAV</option>
                <option value="maintenance">Maintenance</option>
                <option value="visite">Visite</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Date prévue</label>
              <input type="date" value={editData.date_prevue} onChange={e => setEditData(p => ({ ...p, date_prevue: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-date" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Priorité</label>
              <select value={editData.priorite} onChange={e => setEditData(p => ({ ...p, priorite: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-priorite">
                <option value="normale">Normale</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Heure début</label>
              <input type="time" value={editData.heure_debut} onChange={e => setEditData(p => ({ ...p, heure_debut: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-heure-debut" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Heure fin</label>
              <input type="time" value={editData.heure_fin} onChange={e => setEditData(p => ({ ...p, heure_fin: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-heure-fin" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 block mb-1">Technicien</label>
              <select value={editData.technicien_id} onChange={e => setEditData(p => ({ ...p, technicien_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="edit-technicien">
                <option value="">— Non assigné —</option>
                {livreurs.map(l => (
                  <option key={l.id} value={l.id}>{l.prenom} {l.nom}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
              <textarea value={editData.description} onChange={e => setEditData(p => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Détails de l'intervention..." data-testid="edit-description" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={saveEdit} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="save-edit-btn">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              Enregistrer
            </button>
            <button onClick={() => setEditing(false)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Close modal */}
      {showClose && (
        <div className="bg-white rounded-xl border-2 border-emerald-300 p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><CheckCircle size={16} className="text-emerald-600" /> Compte-rendu de clôture</h3>
          <textarea value={compteRendu} onChange={e => setCompteRendu(e.target.value)} placeholder="Décrivez le travail effectué..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="compte-rendu-input" />
          <div className="flex gap-2 mt-3">
            <button onClick={() => changeStatut('terminee', 'Intervention terminée')} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="confirm-close-btn">Confirmer la clôture</button>
            <button onClick={() => setShowClose(false)} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
          </div>
        </div>
      )}

      {/* Compte-rendu (if terminated) */}
      {intervention.statut === 'terminee' && intervention.compte_rendu && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs font-medium text-emerald-700 mb-1">Compte-rendu</p>
          <p className="text-sm text-emerald-900">{intervention.compte_rendu}</p>
        </div>
      )}

      {/* Notes terrain */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm" data-testid="notes-section">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <MessageSquare size={18} className="text-gray-500" />
          Notes terrain
          <span className="text-sm text-gray-400">({notes.length})</span>
        </h3>

        {/* Add note */}
        <div className="flex gap-2 mb-4">
          <input value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} placeholder="Ajouter une note..." className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]" data-testid="note-input" />
          <button onClick={addNote} disabled={sendingNote || !noteText.trim()} className="flex items-center gap-1 px-3 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] disabled:opacity-50" data-testid="send-note-btn">
            {sendingNote ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>

        {notes.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-4">Aucune note pour le moment</p>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {[...notes].reverse().map(note => (
              <div key={note.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-[hsl(222,47%,11%)] rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={14} className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-medium text-gray-700">{note.auteur}</span>
                    <span>{formatFull(note.date)}</span>
                  </div>
                  <p className="text-sm text-gray-900 mt-0.5">{note.texte}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photos SAV */}
      <div
        className={`bg-white rounded-xl border-2 p-5 shadow-sm transition-colors ${isDragging ? 'border-orange-400 bg-orange-50/50' : 'border-[hsl(214,32%,91%)]'}`}
        data-testid="photos-sav-section"
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Camera size={18} className="text-gray-500" />
            Photos SAV
            <span className="text-sm text-gray-400">({photos.length})</span>
          </h3>
          <div className="flex items-center gap-2">
            <input value={legende} onChange={e => setLegende(e.target.value)} placeholder="Légende..." className="px-2 py-1 border border-gray-200 rounded-lg text-xs w-36 focus:outline-none focus:ring-1 focus:ring-orange-400" data-testid="photo-legende-input" />
            <label className={`cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${uploadingPhoto ? 'bg-gray-100 text-gray-400' : 'bg-orange-500 text-white hover:bg-orange-600'}`}>
              {uploadingPhoto ? <><RefreshCw size={14} className="animate-spin" />Upload...</> : <><Upload size={14} />Ajouter</>}
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileInput} disabled={uploadingPhoto} data-testid="photo-sav-upload-input" />
            </label>
          </div>
        </div>

        {isDragging && (
          <div className="flex flex-col items-center justify-center py-8 mb-4 border-2 border-dashed border-orange-400 rounded-xl bg-orange-50 text-orange-600">
            <Upload size={28} className="mb-2" />
            <p className="font-medium text-sm">Relâchez pour ajouter les photos</p>
          </div>
        )}

        {!isDragging && photos.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
            <Camera size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune photo SAV</p>
            <p className="text-xs mt-1">Glissez-déposez des images ou cliquez sur "Ajouter"</p>
          </div>
        ) : !isDragging && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map(photo => (
              <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-100" data-testid={`photo-sav-${photo.id}`}>
                <div className="aspect-square cursor-pointer flex items-center justify-center" onClick={() => setSelectedPhoto(photo)}>
                  <img src={photo.thumbnail_url || `${api.defaults.baseURL}/documents/${photo.id}/download`} alt={photo.legende || photo.filename_original || 'Photo'} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                  <div style={{display:'none'}} className="items-center justify-center w-full h-full text-gray-400 absolute inset-0"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  {photo.legende && <p className="text-white text-xs font-medium truncate">{photo.legende}</p>}
                  <p className="text-white/70 text-[10px]">{formatDate(photo.created_at)}</p>
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setSelectedPhoto(photo)} className="p-1.5 bg-white/90 rounded-lg hover:bg-white shadow-sm"><ZoomIn size={14} className="text-gray-700" /></button>
                  <button onClick={() => deletePhoto(photo.id)} className="p-1.5 bg-white/90 rounded-lg hover:bg-red-50 shadow-sm"><Trash2 size={14} className="text-red-500" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelectedPhoto(null)} className="absolute -top-10 right-0 text-white hover:text-gray-300"><X size={24} /></button>
            <img src={selectedPhoto.thumbnail_url || `${api.defaults.baseURL}/documents/${selectedPhoto.id}/download`} alt={selectedPhoto.legende || 'Photo'} className="w-full h-auto max-h-[85vh] object-contain rounded-lg bg-gray-900" />
            <div className="mt-3 text-white text-sm">
              {selectedPhoto.legende && <p className="font-medium">{selectedPhoto.legende}</p>}
              <p className="text-white/60 text-xs">{selectedPhoto.filename_original} — {formatDate(selectedPhoto.created_at)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-5 shadow-sm" data-testid="historique-section">
        <h3 className="font-semibold text-gray-900 mb-4">Historique</h3>
        {intervention.historique?.length > 0 ? (
          <div className="space-y-2">
            {intervention.historique.map((h, i) => {
              const s = STATUT_CONFIG[h.statut] || {};
              return (
                <div key={`${h.statut}-${h.date}-${i}`} className="flex items-center gap-3 text-sm">
                  <span className={`w-2 h-2 rounded-full ${s.bg || 'bg-gray-200'}`} />
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.bg || ''} ${s.text || ''}`}>{s.label || h.statut}</span>
                  <span className="text-gray-400">{formatFull(h.date)}</span>
                  <span className="text-gray-500">— {h.note || h.par}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">Aucun historique</p>
        )}
      </div>
    </div>
  );
}
